/**
 * The coin-refund rule (ClickUp 869f7jkj5), mirrored from
 * apps/api-py/app/models/refund_calculator.py for the live PREVIEW only — the
 * API recomputes on confirm against the live wallet and never trusts this.
 *
 *   refund    = price × percent           (half-up to the cent)
 *   coins cut = coins in the pack × percent (half-up to a whole coin)
 *   balance   = wallet − coins cut, never below 0
 *
 * Integer arithmetic for both roundings, so this and the Python agree exactly.
 * See the Python module's docstring for why the balance stops at 0 and what
 * `shortfall` / `fairPercent` tell the agent.
 */

export const DEFAULT_REFUND_PERCENT = 50;
export const MAX_REFUND_PERCENT = 100;
export const MIN_REFUND_PERCENT = 1;

export interface RefundInput {
  priceCents: number;
  coinsPurchased: number;
  balance: number;
  percent: number;
}

export interface RefundQuote {
  percent: number;
  refundCents: number;
  coinsToCut: number;
  coinsDeducted: number;
  balanceBefore: number;
  balanceAfter: number;
  shortfall: number;
  fairPercent: number;
}

function shareHalfUp(amount: number, percent: number): number {
  return Math.floor((amount * percent + 50) / 100);
}

export function quoteRefund({ priceCents, coinsPurchased, balance, percent }: RefundInput): RefundQuote {
  if (!Number.isInteger(priceCents) || priceCents <= 0) {
    throw new Error('Purchase amount must be greater than zero.');
  }
  if (!Number.isInteger(coinsPurchased) || coinsPurchased <= 0) {
    throw new Error('Coins in the purchase must be a whole number greater than zero.');
  }
  if (!Number.isInteger(percent) || percent < MIN_REFUND_PERCENT || percent > MAX_REFUND_PERCENT) {
    throw new Error(`Refund share must be a whole percent from ${MIN_REFUND_PERCENT} to ${MAX_REFUND_PERCENT}.`);
  }
  if (!Number.isFinite(balance)) throw new Error('Balance must be a number.');
  const held = Math.max(balance, 0);
  const coinsToCut = shareHalfUp(coinsPurchased, percent);
  const coinsDeducted = Math.min(coinsToCut, held);
  return {
    percent,
    refundCents: shareHalfUp(priceCents, percent),
    coinsToCut,
    coinsDeducted,
    balanceBefore: held,
    balanceAfter: held - coinsDeducted,
    shortfall: coinsToCut - coinsDeducted,
    fairPercent: Math.min(MAX_REFUND_PERCENT, Math.floor((held * 100) / coinsPurchased)),
  };
}

/** Minor units -> "$24.50" for USD, "24.50 EUR" for another code, "24.50"
 * when the currency is unknown (numbers typed by hand). */
export function formatMoney(cents: number, currency: string | null | undefined): string {
  const major = (cents / 100).toFixed(2);
  if (!currency) return major;
  if (currency.toUpperCase() === 'USD') return `$${major}`;
  return `${major} ${currency.toUpperCase()}`;
}

/** "24.50" typed by the agent -> 2450 minor units; null when it is not a
 * positive amount with at most two decimals. */
export function parseMoneyToCents(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const [whole, frac = ''] = trimmed.split('.');
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  return cents > 0 ? cents : null;
}
